// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { CartUnavailableProduct } from '@iheartjane/data-access';
import { PRICE_ID_TO_GRAMS } from '@iheartjane/menu';
import {
  Box,
  CheckCircledIcon,
  AlertIcon,
  Flex,
  Typography,
} from '@iheartjane/reefer';
import type { CheckoutErrorDetails } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import isEmpty from 'lodash-es/isEmpty';
import { useMemo } from 'react';
import type { DeepReadonly } from 'ts-essentials';

import { useBrandSpecialsContext } from '@jane/shared-ecomm/providers';
/* eslint-disable-next-line no-restricted-imports */
import type { Cart, Store } from '@jane/shared/models';

import { checkoutErrorCopy } from './checkoutErrorCopy';

const RemovedRewardListItem = styled(Box)({
  listStyleType: 'none',
  textAlign: 'left',
  '&::before': {
    content: '"•"',
    position: 'absolute' as const,
    left: -12,
  },
});

function andify(array: string[]) {
  const copy = [...array];
  copy.push('and ' + copy.pop());
  return copy.join(', ');
}

function sentenceify(array: string[]) {
  switch (array.length) {
    case 0:
      return '';
    case 1:
      return array[0];
    case 2:
      return array.join(' and ');
    default:
      return andify(array);
  }
}

const unavailableProductsText = (
  cart: DeepReadonly<Cart>,
  store: DeepReadonly<Store> | null
) => {
  const { unavailable_products } = cart;
  const storeName = store?.name ? ` at ${store.name}` : '';

  const withoutDuplicates = unavailable_products.reduce(
    (prev: CartUnavailableProduct[], curr) =>
      prev.some(
        (product) =>
          product.product_id === curr.product_id &&
          product.price_id === curr.price_id
      )
        ? prev
        : [...prev, curr],
    []
  );

  if (!cart.products.length) {
    return `All of your selected products have sold out or are no longer available${storeName}.
        Please start a new bag.`;
  } else {
    const productNames = sentenceify(
      withoutDuplicates.map((p) =>
        p.price_id === 'each'
          ? `${p.name}`
          : `${PRICE_ID_TO_GRAMS[p.price_id]}g ${p.name}`
      )
    );
    const verb = withoutDuplicates.length === 1 ? 'is' : 'are';
    return (
      <span>
        <em>{productNames}</em> {verb} no longer available{storeName}.
      </span>
    );
  }
};

interface Props {
  cart: DeepReadonly<Cart>;
  checkoutErrorDetails?: CheckoutErrorDetails | null;
  isFixingUnavailableCheckout?: boolean;
  noTitle?: boolean;
  store: DeepReadonly<Store> | null;
}

export const CartAlertsForm = ({
  cart,
  checkoutErrorDetails,
  isFixingUnavailableCheckout,
  noTitle,
  store,
}: Props) => {
  const { addedBrandSpecialIds, removedBrandSpecialIds } =
    useBrandSpecialsContext();

  const errorCopy = useMemo(
    () => checkoutErrorCopy(checkoutErrorDetails),
    [checkoutErrorDetails]
  );

  const {
    brand_discounts_total,
    brand_special_has_changed,
    brand_special_unavailable_reason,
    products,
    special_has_changed,
    removed_crm_redemptions,
    unavailable_products,
  } = cart;

  const getTitle = () => {
    switch (true) {
      case !!errorCopy:
        return "Your order wasn't placed";
      case brand_special_has_changed:
        return 'Bag updated';
      case isFixingUnavailableCheckout:
        return 'Your bag has changed';
      default:
        return 'Bag may have changed';
    }
  };

  const janeGoldAlert = useMemo(() => {
    if (errorCopy) {
      return null;
    }

    // Ahead of the guard: the added/removed ids come from a context that may not have resolved.
    if (brand_special_unavailable_reason) {
      return 'Jane Gold cash back is no longer available on this order.';
    }

    if (!brand_special_has_changed || removedBrandSpecialIds === undefined) {
      return null;
    }

    if (brand_discounts_total !== 0 && removedBrandSpecialIds.length > 0) {
      return 'You are no longer eligible for some of your Jane Gold cash back rewards.';
    }

    if (brand_discounts_total !== 0 && addedBrandSpecialIds.length === 1) {
      return 'You are now eligible for a new Jane Gold cash back reward.';
    }

    if (brand_discounts_total !== 0 && addedBrandSpecialIds.length > 1) {
      return 'You are now eligible for new Jane Gold rewards.';
    }

    if (removedBrandSpecialIds.length > 0) {
      return 'You are no longer eligible for your Jane Gold cash back rewards.';
    }

    return 'Your Jane Gold cash back rewards have been updated.';
  }, [
    addedBrandSpecialIds.length,
    brand_discounts_total,
    brand_special_has_changed,
    brand_special_unavailable_reason,
    errorCopy,
    removedBrandSpecialIds,
  ]);

  // The button deletes unavailable products whatever is shown, so they must still be named.
  const hasCartChanges =
    !!unavailable_products.length ||
    !products.length ||
    !!special_has_changed ||
    !!removed_crm_redemptions?.length;

  if (errorCopy && !hasCartChanges) {
    return (
      <div data-testid="cart-alert-form">
        <Flex flexDirection="column" alignItems="center" gap={24}>
          {!noTitle && (
            <Typography branded textAlign="center" variant="h2-semibold">
              {getTitle()}
            </Typography>
          )}
          <Flex flexDirection="column" gap={8}>
            <Typography>{errorCopy.body}</Typography>
            <Typography>{errorCopy.detail}</Typography>
          </Flex>
        </Flex>
      </div>
    );
  }

  return (
    <div data-testid="cart-alert-form">
      <Flex flexDirection="column" alignItems="center" gap={24}>
        {brand_special_has_changed && !!addedBrandSpecialIds?.length && (
          <CheckCircledIcon size="xxl" color="primary" />
        )}
        {brand_special_has_changed &&
          !!removedBrandSpecialIds?.length &&
          !addedBrandSpecialIds?.length && (
            <AlertIcon size="xxl" color="primary" />
          )}
        {!noTitle && (
          <Typography branded textAlign="center" variant="h2-semibold">
            {getTitle()}
          </Typography>
        )}
        <Flex flexDirection="column" gap={24}>
          {errorCopy && (
            <Flex flexDirection="column" gap={8}>
              <Typography>{errorCopy.body}</Typography>
              <Typography>{errorCopy.detail}</Typography>
            </Flex>
          )}

          {(!products.length || !!unavailable_products.length) && (
            <Typography>{unavailableProductsText(cart, store)}</Typography>
          )}

          {janeGoldAlert && (
            <Typography>
              {janeGoldAlert} View your bag for more information.
            </Typography>
          )}

          {special_has_changed && (
            <Typography>
              Specials applied to one or more of your selected products have
              changed.
            </Typography>
          )}

          {removed_crm_redemptions && removed_crm_redemptions.length > 0 && (
            <Box>
              <Typography>
                The following rewards have been removed from your bag:
              </Typography>
              <ul>
                {removed_crm_redemptions.map((redemption) => (
                  <RemovedRewardListItem
                    as="li"
                    key={redemption.id}
                    mb={8}
                    position="relative"
                    left={12}
                  >{`${redemption.reward_name} (${
                    redemption.point_value
                  } ${pluralize({
                    noun: 'pt',
                    number: redemption.point_value,
                    returnValue: 'word',
                  })})`}</RemovedRewardListItem>
                ))}
              </ul>
            </Box>
          )}

          {isEmpty(products) && !isEmpty(unavailable_products) && (
            <Typography>{`Please review your ${
              isFixingUnavailableCheckout ? 'order' : 'bag'
            } for more details.`}</Typography>
          )}
        </Flex>
      </Flex>
    </div>
  );
};
